import { getGroupAmenitiesApiExploreGroupsGroupIdAmenitiesGet, createGroupAmenityApiExploreGroupsGroupIdAmenitiesPost } from '@/client/sdk.gen';
/**
 * Explore Module API Wrapper
 *
 * This file provides a clean interface to the explore module API endpoints.
 * All API calls for the explore/home search flow should go through here.
 */
import {
  createGroupApiExploreGroupsPost,
  getGroupApiExploreGroupsGroupIdGet,
  getGroupsApiExploreGroupsGet,
  updateGroupApiExploreGroupsGroupIdPatch,
  deleteGroupApiExploreGroupsGroupIdDelete,
  uploadGroupCoverApiExploreGroupsGroupIdCoverPost,
  removeGroupCoverApiExploreGroupsGroupIdCoverDelete,
  createInviteApiExploreGroupsGroupIdInvitesPost,
  getInvitesApiExploreGroupsGroupIdInvitesGet,
  getUserInvitesApiExploreInvitesGet,
  updateInviteApiExploreInvitesInviteIdPatch,
  acceptInvitePublicApiExploreInvitesTokenAcceptPost,
  declineInvitePublicApiExploreInvitesTokenDeclinePost,
  resendInviteApiExploreInvitesInviteIdResendPost,
  getMembersApiExploreGroupsGroupIdMembersGet,
  getMyPreferencesApiExploreGroupsGroupIdMembersMePreferencesGet,
  getGroupPreferencesApiExploreGroupsGroupIdMemberPreferencesGet,
  removeMemberApiExploreMembersMemberIdDelete,
  upsertMyPreferencesApiExploreGroupsGroupIdMembersMePreferencesPut,
  createListingApiExploreGroupsGroupIdListingsPost,
  deleteListingApiExploreListingsListingIdDelete,
  getCommentsApiExploreListingsListingIdCommentsGet,
  createCommentApiExploreListingsListingIdCommentsPost,
  updateCommentApiExploreCommentsCommentIdPatch,
  deleteCommentApiExploreCommentsCommentIdDelete,
  getVotesApiExploreListingsListingIdVotesGet,
  createVoteApiExploreListingsListingIdVotesPost,
  updateVoteApiExploreVotesVoteIdPatch,
  deleteVoteApiExploreVotesVoteIdDelete,
  updateListingApiExploreListingsListingIdPatch,
  getHomeTypesApiHomeTypesGet,
  getHomeTypesApiExploreOptionsHomeTypesGet,
  getPropertyTypesApiExploreOptionsPropertyTypesGet,
  getAmenitiesApiExploreOptionsAmenitiesGet,
  getCurrenciesApiExploreOptionsCurrenciesGet,
  getListingVisitsApiExploreListingsListingIdVisitsGet,
  createListingVisitApiExploreListingsListingIdVisitsPost,
  updateListingVisitApiExploreVisitsVisitIdPatch,
  deleteListingVisitApiExploreVisitsVisitIdDelete,
  createListingVisitNoteApiExploreVisitsVisitIdNotesPost,
  updateListingVisitNoteApiExploreVisitNotesNoteIdPatch,
  deleteListingVisitNoteApiExploreVisitNotesNoteIdDelete,
  uploadListingVisitNotePhotoApiExploreVisitNotesNoteIdPhotosUploadPost,
  deleteListingVisitNotePhotoApiExploreVisitNotePhotosPhotoIdDelete,
} from "@/client";
import type {
  AmenityItem,
  CommentCreate,
  CommentResponse,
  CommentUpdate,
  CurrencyItem,
  ExploreGroupCreate,
  ExploreGroupListItem,
  ExploreGroupResponse,
  ExploreGroupUpdate,
  GroupInviteCreate,
  HomeTypeResponse,
  ListingVisitCreate,
  ListingVisitNoteCreate,
  ListingVisitNotePhotoResponse,
  ListingVisitNoteResponse,
  ListingVisitNoteUpdate,
  ListingVisitResponse,
  ListingVisitUpdate,
  GroupInviteUpdate,
  GroupInviteResponse,
  GroupMemberPreferenceResponse,
  GroupMemberPreferenceUpsert,
  GroupMemberResponse,
  PropertyListingCreate,
  PropertyListingUpdate,
  PropertyListingResponse,
  ReferenceItem,
  VoteCreate,
  VoteResponse,
  VoteUpdate,
} from "@/client";

export {
  enrichListing,
  extractAndCreateListing,
  getListing,
  getListings,
} from '@/lib/listing-api';

export type GroupMemberWithName = GroupMemberResponse & {
  name?: string | null;
};

// ============================================================================
// Group API
// ============================================================================

/**
 * Create a new explore group
 */
export async function createGroup(
  data: ExploreGroupCreate,
): Promise<ExploreGroupResponse> {
  const response = await createGroupApiExploreGroupsPost({ body: data });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Get a group by ID
 */
export async function getGroup(groupId: string): Promise<ExploreGroupResponse> {
  const response = await getGroupApiExploreGroupsGroupIdGet({
    path: { group_id: groupId },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Get all groups for the current user, enriched with aggregates (members,
 * saved-listing cover photos, pending-vote count for the viewer, etc.).
 */
export async function getGroups(): Promise<ExploreGroupListItem[]> {
  const response = await getGroupsApiExploreGroupsGet();
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Update a group
 */
export async function updateGroup(
  groupId: string,
  data: ExploreGroupUpdate,
): Promise<ExploreGroupResponse> {
  const response = await updateGroupApiExploreGroupsGroupIdPatch({
    path: { group_id: groupId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

export async function uploadGroupCover(groupId: string, file: File): Promise<ExploreGroupResponse> {
  const response = await uploadGroupCoverApiExploreGroupsGroupIdCoverPost({
    path: { group_id: groupId },
    body: { file },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

export async function removeGroupCover(groupId: string): Promise<ExploreGroupResponse> {
  const response = await removeGroupCoverApiExploreGroupsGroupIdCoverDelete({
    path: { group_id: groupId },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Delete a group
 */
export async function deleteGroup(groupId: string): Promise<void> {
  const response = await deleteGroupApiExploreGroupsGroupIdDelete({
    path: { group_id: groupId },
  });
  if (response.error) {
    throw response.error;
  }
}

// ============================================================================
// Invite API
// ============================================================================

/**
 * Create an invite for a group
 */
export async function createInvite(
  groupId: string,
  data: GroupInviteCreate,
): Promise<GroupInviteResponse> {
  const response = await createInviteApiExploreGroupsGroupIdInvitesPost({
    path: { group_id: groupId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Get all invites for a group
 */
export async function getInvites(
  groupId: string,
): Promise<GroupInviteResponse[]> {
  const response = await getInvitesApiExploreGroupsGroupIdInvitesGet({
    path: { group_id: groupId },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Get all invites for the current user
 */
export async function getUserInvites(): Promise<GroupInviteResponse[]> {
  const response = await getUserInvitesApiExploreInvitesGet({});
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Update an invite status (accept/reject/revoke)
 */
export async function updateInvite(
  inviteId: string,
  data: GroupInviteUpdate,
): Promise<GroupInviteResponse> {
  const response = await updateInviteApiExploreInvitesInviteIdPatch({
    path: { invite_id: inviteId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Accept an invite using a public token
 */
export async function acceptInvitePublic(
  inviteToken: string,
): Promise<GroupInviteResponse> {
  const response = await acceptInvitePublicApiExploreInvitesTokenAcceptPost({
    body: { invite_token: inviteToken },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Decline an invite using a public token
 */
export async function declineInvitePublic(
  inviteToken: string,
): Promise<GroupInviteResponse> {
  const response = await declineInvitePublicApiExploreInvitesTokenDeclinePost({
    body: { invite_token: inviteToken },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Resend an existing invitation email
 */
export async function resendInvite(
  inviteId: string,
): Promise<GroupInviteResponse> {
  const response = await resendInviteApiExploreInvitesInviteIdResendPost({
    path: { invite_id: inviteId },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

// ============================================================================
// Member API
// ============================================================================

/**
 * Get all members of a group
 */
export async function getMembers(
  groupId: string,
): Promise<GroupMemberWithName[]> {
  const response = await getMembersApiExploreGroupsGroupIdMembersGet({
    path: { group_id: groupId },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Remove a member from a group
 */
export async function removeMember(memberId: string): Promise<void> {
  const response = await removeMemberApiExploreMembersMemberIdDelete({
    path: { member_id: memberId },
  });
  if (response.error) {
    throw response.error;
  }
}

/**
 * Get current user's onboarding preferences for a group
 */
export async function getMyPreferences(
  groupId: string,
): Promise<GroupMemberPreferenceResponse> {
  const response =
    await getMyPreferencesApiExploreGroupsGroupIdMembersMePreferencesGet({
      path: { group_id: groupId },
    });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

export async function getGroupPreferences(
  groupId: string,
): Promise<GroupMemberPreferenceResponse[]> {
  const response =
    await getGroupPreferencesApiExploreGroupsGroupIdMemberPreferencesGet({
      path: { group_id: groupId },
    });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Save current user's onboarding preferences for a group
 */
export async function upsertMyPreferences(
  groupId: string,
  data: GroupMemberPreferenceUpsert,
): Promise<GroupMemberPreferenceResponse> {
  const response =
    await upsertMyPreferencesApiExploreGroupsGroupIdMembersMePreferencesPut({
      path: { group_id: groupId },
      body: data,
    });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

// ============================================================================
// Listing API
// ============================================================================

/**
 * Create a listing for a group
 */
export async function createListing(
  groupId: string,
  data: PropertyListingCreate,
): Promise<PropertyListingResponse> {
  const response = await createListingApiExploreGroupsGroupIdListingsPost({
    path: { group_id: groupId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Update an existing listing
 */
export async function updateListing(
  listingId: string,
  data: PropertyListingUpdate,
): Promise<PropertyListingResponse> {
  const response = await updateListingApiExploreListingsListingIdPatch({
    path: { listing_id: listingId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Delete a listing
 */
export async function deleteListing(listingId: string): Promise<void> {
  const response = await deleteListingApiExploreListingsListingIdDelete({
    path: { listing_id: listingId },
  });
  if (response.error) {
    throw response.error;
  }
}

/**
 * Legacy /api/home/types lookup — kept for the listing form. The
 * questionnaire onboarding flow uses ``getHomeTypeOptions``
 * (below) against the explore catalog instead.
 */
export async function getHomeTypes(): Promise<HomeTypeResponse[]> {
  const response = await getHomeTypesApiHomeTypesGet();
  if (response.error) {
    throw response.error;
  }
  return response.data ?? [];
}

// ============================================================================
// Explore Options Catalog API
// ============================================================================
//
// Read-only catalog endpoints powering the questionnaire option sets.
// Each row is a ReferenceItem ({ id, code, i18n_key }) plus table-specific
// fields where relevant. The FE resolves user-facing labels by passing
// `i18n_key` to react-intl + Crowdin — never display the `code` directly.
// Responses set Cache-Control: public, max-age=3600 server-side, so callers
// should pair these with a matching react-query staleTime.

/**
 * Active home-type options for the questionnaire.
 */
export async function getHomeTypeOptions(): Promise<ReferenceItem[]> {
  const response = await getHomeTypesApiExploreOptionsHomeTypesGet();
  if (response.error) {
    throw response.error;
  }
  return response.data ?? [];
}

/**
 * Active property-type (primary residence / vacation / investment) options.
 */
export async function getPropertyTypeOptions(): Promise<ReferenceItem[]> {
  const response = await getPropertyTypesApiExploreOptionsPropertyTypesGet();
  if (response.error) {
    throw response.error;
  }
  return response.data ?? [];
}

/**
 * Active amenities. With ``category`` set, returns rows tagged for that
 * bucket plus rows tagged ``both``; without it returns every active amenity.
 */
export async function getAmenityOptions(
  category?: "must_have" | "nice_to_have",
): Promise<AmenityItem[]> {
  const response = await getAmenitiesApiExploreOptionsAmenitiesGet(
    category ? { query: { category } } : undefined,
  );
  if (response.error) {
    throw response.error;
  }
  return response.data ?? [];
}

/**
 * Active currencies (ISO-4217), carrying ``symbol`` for Intl formatting.
 */
export async function getCurrencyOptions(): Promise<CurrencyItem[]> {
  const response = await getCurrenciesApiExploreOptionsCurrenciesGet();
  if (response.error) {
    throw response.error;
  }
  return response.data ?? [];
}

// ============================================================================
// Listing Votes API
// ============================================================================

/**
 * Get votes for a listing
 */
export async function getVotes(listingId: string): Promise<VoteResponse[]> {
  const response = await getVotesApiExploreListingsListingIdVotesGet({
    path: { listing_id: listingId },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Create a vote for a listing
 */
export async function createVote(
  listingId: string,
  data: VoteCreate,
): Promise<VoteResponse> {
  const response = await createVoteApiExploreListingsListingIdVotesPost({
    path: { listing_id: listingId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Update a vote
 */
export async function updateVote(
  voteId: string,
  data: VoteUpdate,
): Promise<VoteResponse> {
  const response = await updateVoteApiExploreVotesVoteIdPatch({
    path: { vote_id: voteId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Delete a vote
 */
export async function deleteVote(voteId: string): Promise<void> {
  const response = await deleteVoteApiExploreVotesVoteIdDelete({
    path: { vote_id: voteId },
  });
  if (response.error) {
    throw response.error;
  }
}

// ============================================================================
// Listing Comments API
// ============================================================================

/**
 * Get comments for a listing
 */
export async function getComments(
  listingId: string,
): Promise<CommentResponse[]> {
  const response = await getCommentsApiExploreListingsListingIdCommentsGet({
    path: { listing_id: listingId },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

/**
 * Create a comment for a listing
 */
export async function createComment(
  listingId: string,
  data: CommentCreate,
): Promise<CommentResponse> {
  const response = await createCommentApiExploreListingsListingIdCommentsPost({
    path: { listing_id: listingId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

export async function updateComment(
  commentId: string,
  data: CommentUpdate,
): Promise<CommentResponse> {
  const response = await updateCommentApiExploreCommentsCommentIdPatch({
    path: { comment_id: commentId },
    body: data,
  });
  if (response.error) {
    throw response.error;
  }
  return response.data;
}

export async function deleteComment(commentId: string): Promise<void> {
  const response = await deleteCommentApiExploreCommentsCommentIdDelete({
    path: { comment_id: commentId },
  });
  if (response.error) {
    throw response.error;
  }
}

// ============================================================================
// Listing Visits and Notes API
// ============================================================================

export async function getListingVisits(
  listingId: string,
): Promise<ListingVisitResponse[]> {
  const response = await getListingVisitsApiExploreListingsListingIdVisitsGet({
    path: { listing_id: listingId },
  });
  if (response.error) throw response.error;
  return response.data;
}

export async function createListingVisit(
  listingId: string,
  data: ListingVisitCreate,
): Promise<ListingVisitResponse> {
  const response =
    await createListingVisitApiExploreListingsListingIdVisitsPost({
      path: { listing_id: listingId },
      body: data,
    });
  if (response.error) throw response.error;
  return response.data;
}

export async function updateListingVisit(
  visitId: string,
  data: ListingVisitUpdate,
): Promise<ListingVisitResponse> {
  const response = await updateListingVisitApiExploreVisitsVisitIdPatch({
    path: { visit_id: visitId },
    body: data,
  });
  if (response.error) throw response.error;
  return response.data;
}

export async function deleteListingVisit(visitId: string): Promise<void> {
  const response = await deleteListingVisitApiExploreVisitsVisitIdDelete({
    path: { visit_id: visitId },
  });
  if (response.error) throw response.error;
}

export async function createListingVisitNote(
  visitId: string,
  data: ListingVisitNoteCreate,
): Promise<ListingVisitNoteResponse> {
  const response = await createListingVisitNoteApiExploreVisitsVisitIdNotesPost(
    {
      path: { visit_id: visitId },
      body: data,
    },
  );
  if (response.error) throw response.error;
  return response.data;
}

export async function updateListingVisitNote(
  noteId: string,
  data: ListingVisitNoteUpdate,
): Promise<ListingVisitNoteResponse> {
  const response = await updateListingVisitNoteApiExploreVisitNotesNoteIdPatch({
    path: { note_id: noteId },
    body: data,
  });
  if (response.error) throw response.error;
  return response.data;
}

export async function deleteListingVisitNote(noteId: string): Promise<void> {
  const response = await deleteListingVisitNoteApiExploreVisitNotesNoteIdDelete(
    {
      path: { note_id: noteId },
    },
  );
  if (response.error) throw response.error;
}

export async function uploadListingVisitNotePhoto(
  noteId: string,
  file: File,
): Promise<ListingVisitNotePhotoResponse> {
  const response =
    await uploadListingVisitNotePhotoApiExploreVisitNotesNoteIdPhotosUploadPost(
      {
        path: { note_id: noteId },
        body: { file },
      },
    );
  if (response.error) throw response.error;
  return response.data;
}

export async function deleteListingVisitNotePhoto(
  photoId: string,
): Promise<void> {
  const response =
    await deleteListingVisitNotePhotoApiExploreVisitNotePhotosPhotoIdDelete({
      path: { photo_id: photoId },
    });
  if (response.error) throw response.error;
}

export async function getGroupAmenities(groupId: string): Promise<AmenityItem[]> {
  const response = await getGroupAmenitiesApiExploreGroupsGroupIdAmenitiesGet({ path: { group_id: groupId } });
  if (response.error) throw response.error;
  return response.data ?? [];
}

export async function createGroupAmenity(groupId: string, label: string): Promise<AmenityItem> {
  const response = await createGroupAmenityApiExploreGroupsGroupIdAmenitiesPost({ path: { group_id: groupId }, body: { label } });
  if (response.error) throw response.error;
  return response.data!;
}
